// Advisory mirror of backend src/app/auth/roles.py. The backend is the
// enforcement source of truth; this module only drives what the UI shows.
// Roles arrive already lowercased on user.roles from GET /auth/user.

export const SECTIONS = {
  GENERAL_INFORMATION: "general_information",
  CAMPAIGNS: "campaigns",
  INTERNAL_PROCEDURES: "internal_procedures",
};

export const CONTENT_SECTION_ROLES = Object.values(SECTIONS);

export const MANAGER = "manager";
export const ADMIN = "admin";

// Fase 6: le a superficie de conversas. Estrutural, nunca um departamento.
export const CONVERSATIONS = "conversations";

// RPAA-286: abre a pagina de analytics do tenant, so leitura (/analytics).
// Estrutural, nunca um departamento.
export const ANALYTICS = "analytics";

// Knowledge-base collection -> section role that gates it. Collections absent
// from this map (e.g. products) are all-access only (default-deny).
export const COLLECTION_SECTION_ROLE = {
  general_information: SECTIONS.GENERAL_INFORMATION,
  campaigns: SECTIONS.CAMPAIGNS,
  internal_procedures: SECTIONS.INTERNAL_PROCEDURES,
};

export const hasRole = (roles, role) =>
  Array.isArray(roles) && roles.includes(role);

// All-access = admin ONLY. super_admin is the platform admin (Tenants
// surface), NOT tenant content access. Do not add it here (spec §2).
export const hasAllAccess = (roles) => hasRole(roles, ADMIN);

// Structural (reserved) roles that are never department keys. Mirrors backend
// RESERVED_BINDING_ROLES: a department role is any free-form key NOT in here.
export const STRUCTURAL_ROLES = new Set([
  "user",
  ADMIN,
  MANAGER,
  "super_admin",
  CONVERSATIONS,
  ANALYTICS,
  ...CONTENT_SECTION_ROLES,
]);

// True if the user holds at least one free-form department role (any role that
// is not structural). Mirrors backend holds_department_role.
export const holdsDepartmentRole = (roles) =>
  Array.isArray(roles) && roles.some((r) => !STRUCTURAL_ROLES.has(r));

// True for users who manage the whole internal_procedures section: admin or the
// internal_procedures section role. They bypass department scoping in the UI
// (see everything, may bind folder roles). Mirrors backend
// is_internal_procedures_manager.
export const isInternalProceduresManager = (roles) =>
  hasAllAccess(roles) || hasRole(roles, SECTIONS.INTERNAL_PROCEDURES);

export const isManager = (roles) => hasRole(roles, MANAGER);

// True if the user may see a gated surface: all-access, or holding the given
// role key. The key is whatever that surface gates on: a content section role,
// MANAGER, or a structural role such as CONVERSATIONS.
export const canSeeSection = (roles, section) =>
  hasAllAccess(roles) || hasRole(roles, section);

// True if the user may manage a knowledge-base collection. Default-deny:
// collections not in COLLECTION_SECTION_ROLE require all-access.
export const canManageCollection = (roles, collection) => {
  if (hasAllAccess(roles)) return true;
  const sectionRole = COLLECTION_SECTION_ROLE[collection];
  if (sectionRole && hasRole(roles, sectionRole)) return true;
  // internal_procedures is also reachable by department members; the folder
  // list/tree then scopes them to their own folders (backend enforces).
  if (collection === SECTIONS.INTERNAL_PROCEDURES && holdsDepartmentRole(roles)) {
    return true;
  }
  return false;
};
